import {SchedulingFlowState} from "./types";
import {ParsedBookingQuery, useParsedBookingQuery} from "../bookingQuery";
import {gql, useMutation} from "@apollo/client";
import {useMemo} from "react";
import {debounce, merge} from "lodash";
import {API_CLIENT} from "@services/graphql/client";
import {api} from "@services/api";
import {useFeatureFlagEvaluation} from "../../../../queries/useFeatureFlagEvaluation";
import {FeatureFlag} from "@services/featureFlagConstants";
import {useAbandonedBookingFlowStateQuery} from "./useBookingProgressQuery";

const convertQueryToSchedulingState = (query: ParsedBookingQuery): SchedulingFlowState => ({
  flowId: query.flowId || undefined,
  specialtyId: query.specialtyId || undefined,
  apptReasonSlug: query.appointmentReasonSlug || undefined,
  locationSlug: query.locationSlug || undefined,
  doctorId: query.doctorId || undefined,
  slotTime: query.selectedTime || undefined,
  region: query.region || undefined,
});

const SCHEDULING_FLOW_STATE_MUTATION = gql`
  mutation SchedulingFlowStateMutation($input: SchedulingFlowState) {
    schedulingFlowState(input: $input)
      @rest(method: "POST", endpoint: "monolith", path: "/hib/visitors/schedulingFlow") {
      id
    }
  }
`;

type Variables = {
  input: SchedulingFlowState;
};

type Data = unknown;

/**
 * This hook can be used to update the booking progress in Redis cache.
 * It appends data from the existing Redis cache entry and query state allowing callers to make partial updates.
 * @returns A tuple of the mutation function and the mutation result.
 */
export const usePostBookingProgressMutation = (dropoutVisitorId?: string) => {
  const schedulingFlowStateQuery = useAbandonedBookingFlowStateQuery({
    variables: {
      visitorId: dropoutVisitorId || "",
    },
    skip: !dropoutVisitorId,
  });

  const schedulingFlowState = schedulingFlowStateQuery.data?.schedulingFlowState;

  const {WEB_SCHEDULING_ABANDONED_FLOW_ENABLED} = useFeatureFlagEvaluation(
    {
      [FeatureFlag.WEB_SCHEDULING_ABANDONED_FLOW_ENABLED]: false,
    },
    // note for future cleanup, move this check to the `postWithContext` function
    // do not post scheduling flow updates if the abandoned flow was already booked
    !!(schedulingFlowState && schedulingFlowState?.isBooked),
  ).data;

  const [postBookingProgress, mutation] = useMutation<Data, Variables>(
    SCHEDULING_FLOW_STATE_MUTATION,
    {
      context: {
        clientName: API_CLIENT,
        headers: api.getDefaultHeaders(undefined, undefined, true),
      },
    },
  );

  const query = useParsedBookingQuery();
  const queryState = useMemo(() => convertQueryToSchedulingState(query), [query]);

  const postWithContext = useMemo(
    () =>
      WEB_SCHEDULING_ABANDONED_FLOW_ENABLED
        ? debounce(
            (schedulingFlowState: SchedulingFlowState) =>
              postBookingProgress({
                variables: {
                  input: merge({isBooked: false}, queryState, schedulingFlowState),
                },
              }),
            700,
          )
        : async () => undefined,
    [WEB_SCHEDULING_ABANDONED_FLOW_ENABLED, postBookingProgress, queryState],
  );

  return [postWithContext, mutation] as const;
};
